import { useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { IoCameraOutline } from 'react-icons/io5';
import { CachedImage, DotMenu, DoubleCheckModal } from '@shared/ui';
import { useHideCategory, useDeleteCategory } from '../../hooks/useCategories';
import { useCreateHomeTile, HomeTileType } from '@modules/home-tiles';
import { AttachLinkToBannerModal } from '@modules/banners';
import ProductBadges from '@modules/products/components/ProductCard/ProductBadges';
import AddToSectionModal from '../AddToSectionModal';
import type { Category } from '../../types';
import './CategoryCard.css';

interface CategoryCardProps {
  category: Category;
  onEdit?: () => void;
  sectionId?: number | 'popular';
  hideMenu?: boolean;
}

export default function CategoryCard({ category, onEdit, sectionId, hideMenu }: CategoryCardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isSuperAdmin = useMemo(() => {
    try {
      const raw = localStorage.getItem('luxus_admin');
      if (!raw) return false;
      const p = JSON.parse(raw) as { role?: string };
      return p.role === 'SUPER_ADMIN';
    } catch {
      return false;
    }
  }, [location.pathname]);
  const hideCategory = useHideCategory();
  const deleteCategory = useDeleteCategory();
  const createHomeTile = useCreateHomeTile();
  const [isAddToSectionModalOpen, setIsAddToSectionModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAttachLinkModalOpen, setIsAttachLinkModalOpen] = useState(false);

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    } else {
      navigate(`/categories/${category.id}/edit`);
    }
  };

  const handleHide = async () => {
    try {
      await hideCategory.mutateAsync({
        id: category.id,
        isHidden: !category.isHidden,
      });
    } catch (error) {

      notifications.show({
        title: 'Ошибка',
        message: 'Ошибка при изменении видимости категории',
        color: 'red',
      });
    }
  };

  const handleDelete = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    await deleteCategory.mutateAsync(category.id);
    setIsDeleteModalOpen(false);
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const handleAddToHome = async () => {
    await createHomeTile.mutateAsync({
      type: HomeTileType.CATEGORY,
      name: category.name,
      path: `${location.pathname}/${category.id}/products`,
      img: category.imageUrl,
    });
  };

  const handleAttachLinkToBanner = () => {
    setIsAttachLinkModalOpen(true);
  };

  const handleAddToSection = () => {
    setIsAddToSectionModalOpen(true);
  };

  // Формируем URL для продуктов категории
  const isWithoutSections = location.pathname === '/sections/without-sections';
  const productsUrl = sectionId === 'popular'
    ? `/sections/popular/categories/${category.id}/products`
    : sectionId
      ? `/sections/${sectionId}/categories/${category.id}/products`
      : isWithoutSections
        ? `/sections/without-sections/categories/${category.id}/products`
        : `/categories/${category.id}/products`;

  const menuItems = [
    {
      label: 'Добавить на главную',
      onClick: handleAddToHome,
    },
    {
      label: 'Прикрепить ссылку к баннеру',
      onClick: handleAttachLinkToBanner,
    },
    {
      label: 'Добавить в секцию',
      onClick: handleAddToSection,
    },
    {
      label: 'Редактировать',
      onClick: handleEdit,
    },
    {
      label: category.isHidden ? 'Показать' : 'Скрыть',
      onClick: handleHide,
    },
    {
      label: 'Удалить',
      onClick: handleDelete,
      isDanger: true,
    },
  ];

  const cardBody = (
    <>
      <div className={`category-card-image ${category.isHidden ? 'category-card-image--hidden' : ''} ${!category.imageUrl ? 'category-card-image--placeholder' : ''}`}>
        {category.imageUrl ? (
          <CachedImage src={category.imageUrl} alt={category.name} loading="lazy" />
        ) : (
          <div className="category-card-camera-icon">
            <IoCameraOutline />
          </div>
        )}
        <div className="category-card-badges" onClick={(e) => e.stopPropagation()}>
          <ProductBadges
            hasDiscount={false}
            isPopular={category.isPopular}
            isHidden={category.isHidden}
            onHiddenClick={handleHide}
          />
        </div>
      </div>
      <div className="category-card-content">
        <h3 className="category-card-title">{category.name}</h3>
      </div>
    </>
  );

  return (
    <>
      <div className="category-card-wrapper">
        {isSuperAdmin ? (
          <div className="category-card category-card--static">{cardBody}</div>
        ) : (
          <Link to={productsUrl} className="category-card">
            {cardBody}
          </Link>
        )}
        {!hideMenu && (
          <div className="category-card-menu" onClick={(e) => e.stopPropagation()}>
            <DotMenu items={menuItems} />
          </div>
        )}
      </div>

      {isAddToSectionModalOpen && <AddToSectionModal
        isOpen={isAddToSectionModalOpen}
        onClose={() => setIsAddToSectionModalOpen(false)}
        categoryId={category.id}
        onSuccess={() => {
          setIsAddToSectionModalOpen(false);
        }}
      />}

      {isDeleteModalOpen && <DoubleCheckModal
        isOpen={isDeleteModalOpen}
        onClose={cancelDelete}
        title="Удалить категорию?"
        message={`Вы уверены, что хотите удалить категорию "${category.name}"?`}
        secondButtonText="Удалить"
        secondButtonAction={confirmDelete}
        isLoading={deleteCategory.isPending}
      />}

      {isAttachLinkModalOpen && <AttachLinkToBannerModal
        isOpen={isAttachLinkModalOpen}
        onClose={() => setIsAttachLinkModalOpen(false)}
        link={productsUrl}
      />}
    </>
  );
}

